import { Copy, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlay";
import { Select } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { OUTPUT_SOURCES, useOutput, type OutputEntry, type OutputSource } from "./output";

const time = (at: number) =>
  new Date(at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

const line = (e: OutputEntry) =>
  `[${time(e.at)}] [${e.source}]${e.level === "info" ? "" : ` [${e.level === "error" ? "erro" : "aviso"}]`} ${e.text}`;

/** Aba Saída: o registro do app nesta sessão, por canal, sempre rolando para o fim. */
export function OutputList() {
  const entries = useOutput((s) => s.entries);
  const clear = useOutput((s) => s.clear);
  const [source, setSource] = useState<OutputSource | "all">("all");
  const list = source === "all" ? entries : entries.filter((e) => e.source === source);
  const end = useRef<HTMLDivElement>(null);
  // Acompanha o fim só se a pessoa já estava no fim (não puxa a rolagem de quem está lendo).
  const stick = useRef(true);
  useEffect(() => {
    if (stick.current) end.current?.scrollIntoView({ block: "end" });
  }, [list.length]);
  const copy = () =>
    void navigator.clipboard.writeText(list.map(line).join("\n")).then(() => toast("Saída copiada."));
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-8 shrink-0 items-center gap-2 px-3">
        <Select
          variant="ghost"
          value={source}
          onChange={(e) => setSource(e.target.value as OutputSource | "all")}
          aria-label="Canal"
        >
          <option value="all">Todos os canais</option>
          {OUTPUT_SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <span className="text-[11.5px] text-fg-subtle">{list.length} registros nesta sessão</span>
        <div className="ml-auto flex items-center gap-0.5">
          <Tip label="Copiar">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={copy}
              disabled={!list.length}
              aria-label="Copiar saída"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <Tip label="Limpar">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={clear}
              disabled={!entries.length}
              aria-label="Limpar saída"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        </div>
      </div>
      <div
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-2 font-mono text-[12px] leading-[1.6]"
        role="log"
        aria-live="polite"
        aria-label="Saída"
      >
        {list.length ? (
          list.map((e) => (
            <div key={e.id} className="flex gap-2 whitespace-pre-wrap break-words">
              <span className="shrink-0 text-fg-subtle">{time(e.at)}</span>
              <span className="w-[104px] shrink-0 truncate text-fg-subtle">{e.source}</span>
              <span
                className={cn(
                  "min-w-0",
                  e.level === "error"
                    ? "text-[var(--danger)]"
                    : e.level === "warn"
                      ? "text-[var(--warning)]"
                      : "text-fg-muted",
                )}
              >
                {e.text}
              </span>
            </div>
          ))
        ) : (
          <p className="py-6 text-center font-sans text-[12.5px] text-fg-subtle">
            Nada registrado ainda. Conexão com o time, terminais, disco do sandbox, Git e IA aparecem aqui.
          </p>
        )}
        <div ref={end} />
      </div>
    </div>
  );
}
